/**
 * 猫の状態遷移。
 * プレイヤーは猫に命令できない（§2.3）。ここに「プレイヤーの意図」を持ち込まないこと。
 */
import { addDropTo } from './drops';
import { BED, TILE, furnitureAt, keyOf, salvageAt } from './grid';
import { JumpGraph, NodeKey, SearchCost, pathTo, searchFrom } from './graph';
import { RngState, rngFloat, rngInt } from './rng';
import { CatState, GameState, GoalReason, Species } from './state';
import { sunAt } from './sun';

/** 足場が変わった猫が、立ち止まってキョロキョロする時間（§5.1 演出としての正解） */
export const STARTLE_SEC = 0.9;

/**
 * 帰巣の間隔（シム内秒）。§2.4 の「時間で急かさない」に反しないこと：
 * これは猫の都合であってプレイヤーの締切ではない。間に合わなくても
 * 猫は餓死も体調不良もしない（§10.4）。
 *
 * 間隔は「上の階に行って戻ってくる余裕があるか」で決める。
 * 短すぎると猫が1階の往復に忙殺されて上に行かなくなり、
 * せっかく開通させた上層が死んだ空間になる（§2.7 が逆に働いてしまう）。
 * 1日240秒に対して、水は1日2回、食事は1.5回くらい。
 */
export const HUNGER_DUE = 170;
export const THIRST_DUE = 120;
export const EAT_SEC = 2.6;
export const DRINK_SEC = 2.0;
/**
 * 猫は一日の大半を寝て過ごす。
 * 「せわしなく動き回る小動物」ではなく「たまに動く置物」に見えるよう、
 * じっとしている時間を長く、動く時間を短くしてある。
 */
export const SLEEP_MIN = 45;
export const SLEEP_MAX = 100;
/** 寝床でなくても、その場でくつろぐ */
export const REST_MIN = 10;
export const REST_MAX = 30;
/** 猫同士のじゃれ合い */
export const TUSSLE_MIN = 4;
export const TUSSLE_MAX = 9;
/**
 * 猫の集会。3匹以上が近くでのんびりしていると、なんとなく座り込んで長居する。
 * 意味も報酬も無い。**混んでいる場所が絵で分かる**という一点のためにある（§2.1）。
 */
export const MEET_MIN = 25;
export const MEET_MAX = 60;
export const MEET_RANGE = 3.2;
/** 次に動き出すまでの間 */
export const IDLE_MIN = 3;
export const IDLE_MAX = 11;
/** 置き去りの物にじゃれる長さと、その間の壊し具合（§3.4 / §3.5） */
export const PLAY_MIN = 6;
export const PLAY_MAX = 14;
export const PLAY_WEAR = 11;
/**
 * 壁の飾りに前足を伸ばしている長さと、その間の叩き具合（§3.4）。
 * 猫が高い所の物を落とすのは習性であって、プレイヤーの指示ではない（§2.3）。
 */
export const SWAT_MIN = 4;
export const SWAT_MAX = 10;
export const SWAT_WEAR = 9;
/**
 * 痕跡（爪とぎ跡・抜け毛）を残す間隔。拾いに行く操作は作らない（§2.5 / §10.8）。
 * 資材の主な出所は「元からある物を壊すこと」なので、こちらは細い供給に留める。
 */
export const TRACE_MIN = 60;
export const TRACE_MAX = 110;
/** 割れた窓から入ってくる演出の長さ（§3.6） */
export const ARRIVE_SEC = 1.4;

/**
 * 設置物の傷み方（§3.5）。
 * ふつうに乗っているだけでも少しずつ傷むが、
 * 詰まって待たされている猫・用事があるのに届かない猫はもっと傷ませる。
 * 結果として「よく壊れる場所＝混んでいる場所」になる。
 */
export const WEAR_IDLE = 0.35;
export const WEAR_QUEUED = 3.5;
export const WEAR_STUCK = 2.5;

/** 前の猫がどくのを待つ上限。これを過ぎたら身をよじってすり抜ける（詰まりを永久化させない） */
export const SQUEEZE_AFTER = 1.2;
const WAIT_STEP = 0.15;

function nodeXY(graph: JumpGraph, key: NodeKey): { x: number; y: number } | null {
  const n = graph.nodes.get(key);
  if (!n) return null;
  return { x: n.c * TILE + TILE / 2, y: (n.r + 1) * TILE };
}

/**
 * 足場が消えた猫を、必ず実在するノードに引き戻す（§5.1 防御3）。
 * 同じ列を下→上の順に探し、それでも無ければ最も近いノードへ。
 */
export function snapToGround(state: GameState, graph: JumpGraph, cat: CatState): void {
  const grid = state.grid;
  const c = Math.max(0, Math.min(grid.cols - 1, Math.round((cat.x - TILE / 2) / TILE)));
  const r = Math.max(0, Math.min(grid.rows - 1, Math.round(cat.y / TILE) - 1));

  let found: NodeKey | null = null;
  for (let rr = r; rr < grid.rows && found === null; rr++) {
    const k = keyOf(grid, c, rr);
    if (graph.nodes.has(k)) found = k;
  }
  for (let rr = r; rr >= 0 && found === null; rr--) {
    const k = keyOf(grid, c, rr);
    if (graph.nodes.has(k)) found = k;
  }
  if (found === null) {
    let best = Infinity;
    for (const [k, n] of graph.nodes) {
      const d = Math.abs(n.c - c) + Math.abs(n.r - r);
      if (d < best) {
        best = d;
        found = k;
      }
    }
  }
  if (found === null) return; // ノードが1つも無い盤面。ここでは何もしない

  const pos = nodeXY(graph, found);
  cat.node = found;
  cat.from = null;
  cat.to = null;
  cat.path = null;
  cat.step = 0;
  cat.goal = null;
  cat.goalReason = 'none';
  cat.mode = 'idle';
  cat.waiting = 0;
  if (pos) {
    cat.x = pos.x;
    cat.y = pos.y;
  }
}

/**
 * その猫が「通りたがらない」度合い（§3.3）。
 * 通れるかどうかはグラフが決める。ここで決めるのは行きたがるかどうか。
 */
export function costFor(state: GameState, cat: CatState, species: Species): SearchCost | undefined {
  const heightFear = species.heightFear ?? 0;
  const crowdFear = species.crowdFear ?? 0;
  if (heightFear === 0 && crowdFear === 0) return undefined;

  const rows = state.grid.rows;
  return {
    node: (n, key) => {
      let extra = 0;
      // 高い所ほど嫌がる（子猫）
      if (heightFear > 0) extra += heightFear * Math.max(0, rows - 4 - n.r) * 0.5;
      // 他の猫の近くを避ける（臆病な猫）
      if (crowdFear > 0) {
        let near = 0;
        for (const other of state.cats) {
          if (other.id === cat.id) continue;
          if (other.node === key) near += 2;
          else if (Math.abs(other.x - (n.c * TILE + TILE / 2)) < TILE * 1.6 && Math.abs(other.y - (n.r + 1) * TILE) < TILE) {
            near += 1;
          }
        }
        extra += crowdFear * near;
      }
      return extra;
    },
  };
}

// ---- 帰巣（§2.7）------------------------------------------------------

type Need = 'food' | 'water';

export function pendingNeed(cat: CatState): Need | null {
  if (cat.thirst >= THIRST_DUE) return 'water';
  if (cat.hunger >= HUNGER_DUE) return 'food';
  return null;
}

function spotOf(state: GameState, need: Need): { c: number; r: number } {
  return need === 'water' ? state.water : state.bowl;
}

/** 餌場・水場は「そのマスと左右1マス」を到着点として扱う */
function atSpot(spot: { c: number; r: number }, c: number, r: number): boolean {
  return r === spot.r && Math.abs(c - spot.c) <= 1;
}

function spotNodes(state: GameState, graph: JumpGraph, need: Need): NodeKey[] {
  const spot = spotOf(state, need);
  const out: NodeKey[] = [];
  for (const [k, n] of graph.nodes) if (atSpot(spot, n.c, n.r)) out.push(k);
  return out;
}

/**
 * 目的地へ届かない時に、一番近づける場所を返す。
 * 「行けないので諦めてその辺をうろつく」ではなく、行き止まりまで行って待つ。
 * 症状（猫だまり）が原因（途切れた動線）の場所に出るようにするため（§2.2 / §10.2）。
 */
function closestToward(
  graph: JumpGraph,
  reach: Map<NodeKey, NodeKey | null>,
  spot: { c: number; r: number },
): NodeKey | null {
  let best: NodeKey | null = null;
  let bestD = Infinity;
  for (const k of reach.keys()) {
    const n = graph.nodes.get(k);
    if (!n) continue;
    const d = Math.abs(n.c - spot.c) + Math.abs(n.r - spot.r);
    if (d < bestD) {
      bestD = d;
      best = k;
    }
  }
  return best;
}

interface GoalPick {
  goal: NodeKey | null;
  reason: GoalReason;
  stuck: boolean;
  /** じゃれる相手（置き去りの物のマス） */
  toy?: NodeKey;
  /** 叩き落としたい壁の飾りの id */
  swat?: number;
}

/** じゃれられる物を探す向き：左右と足元 */
const NEIGHBOURS = [
  [-1, 0],
  [1, 0],
  [0, 1],
] as const;

/**
 * 行き先を決める。
 * 用事（腹が減った・のどが渇いた）が最優先で、無ければ日向か、なんとなく。
 * 快適さを単一スカラーで持たない（§2.1 / §10.1）ので、
 * 「どこが良いか」は場所そのものの条件で判定する。
 */
function pickGoal(
  state: GameState,
  graph: JumpGraph,
  cat: CatState,
  species: Species,
  reach: Map<NodeKey, NodeKey | null>,
  rng: RngState,
): GoalPick {
  const need = pendingNeed(cat);
  if (need) {
    const targets = spotNodes(state, graph, need).filter((k) => reach.has(k));
    if (targets.length) {
      const here = graph.nodes.get(cat.node);
      let best = targets[0];
      let bestD = Infinity;
      for (const k of targets) {
        const n = graph.nodes.get(k)!;
        const d = here ? Math.abs(n.c - here.c) + Math.abs(n.r - here.r) : 0;
        if (d < bestD) {
          bestD = d;
          best = k;
        }
      }
      return { goal: best, reason: need, stuck: false };
    }
    // 届かない。行き止まりまで行って、そこで待つ
    return { goal: closestToward(graph, reach, spotOf(state, need)), reason: need, stuck: true };
  }

  const keys = [...reach.keys()];
  if (keys.length < 2) return { goal: null, reason: 'none', stuck: false };

  // 空いている寝床があれば、そこで寝たい
  const beds: NodeKey[] = [];
  for (const k of keys) {
    const n = graph.nodes.get(k);
    if (!n) continue;
    if (furnitureAt(state.grid, n.c, n.r) !== BED) continue;
    if (state.cats.some((o) => o.id !== cat.id && o.node === k)) continue;
    beds.push(k);
  }
  if (beds.length && rngFloat(rng) < 0.5) {
    return { goal: beds[rngInt(rng, beds.length)], reason: 'bed', stuck: false };
  }

  // 日向は時間帯で動く（§3.8）。同じ場所の価値が朝と夕方で変わる
  const band = sunAt(state).band;
  // 置き去りの物にじゃれたい。これが資材の主な出所になる（§3.4）
  const toys: { at: NodeKey; toy: NodeKey }[] = [];
  for (const k of keys) {
    const n = graph.nodes.get(k);
    if (!n) continue;
    for (const [dc, dr] of NEIGHBOURS) {
      const tc = n.c + dc;
      const tr = n.r + dr;
      if (salvageAt(state.grid, tc, tr) > 0) toys.push({ at: k, toy: keyOf(state.grid, tc, tr) });
    }
  }
  // 壁の飾りに前足を伸ばしたい。届く高さは種類ごとに違う（§3.3）
  const swats: { at: NodeKey; swat: number }[] = [];
  if (state.fixtures.length) {
    for (const k of keys) {
      const n = graph.nodes.get(k);
      if (!n) continue;
      const id = fixtureInReachAt(state, n.c, n.r, species.paw);
      if (id !== null) swats.push({ at: k, swat: id });
    }
  }

  // 見晴らしのいい場所に座りたい（猫は高くて端っこが好き）
  const perches: NodeKey[] = [];
  for (const k of keys) {
    const n = graph.nodes.get(k);
    if (!n) continue;
    if (isPerch(state, n.c, n.r)) perches.push(k);
  }
  if (perches.length && rngFloat(rng) < 0.25) {
    return { goal: perches[rngInt(rng, perches.length)], reason: 'perch', stuck: false };
  }

  if (toys.length && rngFloat(rng) < 0.34) {
    const pick = toys[rngInt(rng, toys.length)];
    return { goal: pick.at, reason: 'play', stuck: false, toy: pick.toy };
  }

  if (swats.length && rngFloat(rng) < 0.34) {
    const pick = swats[rngInt(rng, swats.length)];
    return { goal: pick.at, reason: 'play', stuck: false, swat: pick.swat };
  }

  const sunny: NodeKey[] = [];
  if (band) {
    for (const k of keys) {
      const n = graph.nodes.get(k);
      if (!n) continue;
      if (n.r === band.row && n.c >= band.c0 && n.c <= band.c1) sunny.push(k);
    }
  }
  if (sunny.length && rngFloat(rng) < 0.45) {
    return { goal: sunny[rngInt(rng, sunny.length)], reason: 'sunny', stuck: false };
  }
  return { goal: keys[rngInt(rng, keys.length)], reason: 'wander', stuck: false };
}

function startMove(
  state: GameState,
  graph: JumpGraph,
  cat: CatState,
  species: Species,
  rng: RngState,
): void {
  if (!graph.nodes.has(cat.node)) {
    snapToGround(state, graph, cat);
    cat.wait = 0.3;
    return;
  }
  // 到達できるかはグラフの形で決まり、どの道を選ぶかは種類ごとのコストで決まる
  const search = searchFrom(graph, cat.node, costFor(state, cat, species));
  const reach = search.prev;
  const { goal, reason, stuck, toy, swat } = pickGoal(state, graph, cat, species, reach, rng);
  cat.stuck = stuck;
  cat.toy = toy ?? null;
  cat.swat = swat ?? null;

  if (goal === null || goal === cat.node) {
    // もう近づけない。その場で待つ（用事があるなら不満そうに）
    cat.goal = null;
    cat.goalReason = stuck ? reason : 'none';
    cat.wait = stuck ? 1.2 + rngFloat(rng) * 1.6 : IDLE_MIN + rngFloat(rng) * (IDLE_MAX - IDLE_MIN);
    return;
  }
  const path = pathTo(reach, goal);
  if (!path || path.length < 2) {
    cat.wait = 0.6;
    return;
  }
  cat.path = path;
  cat.step = 0;
  cat.goal = goal;
  cat.goalReason = reason;
  nextLeg(state, graph, cat, species, rng);
}

/** そのマスに他の猫が立っている（または向かっている）か */
function occupiedByOther(state: GameState, cat: CatState, key: NodeKey): boolean {
  for (const other of state.cats) {
    if (other.id === cat.id) continue;
    if (other.node === key) return true;
  }
  return false;
}

/**
 * そのマスに立った猫の前足が届く飾り（§3.3）。
 * 「立っているマスと左右1マス」の、頭上 paw マスまで。
 * world 側の fixturesInReach と同じ規則。ここは経路探索から呼ぶので純粋関数にしてある。
 */
export function fixtureInReachAt(
  state: GameState,
  c: number,
  r: number,
  paw: number,
): number | null {
  for (const f of state.fixtures) {
    if (Math.abs(f.c - c) > 1) continue;
    const up = r - f.r;
    if (up < 0 || up > paw) continue;
    return f.id;
  }
  return null;
}

/** 見晴らしのいい場所か（縁に立っていて、下が抜けている） */
export function isPerch(state: GameState, c: number, r: number): boolean {
  const grid = state.grid;
  for (const dc of [-1, 1]) {
    let depth = 0;
    while (depth < 4 && !isSolidAt(grid, c + dc, r + 1 + depth)) depth++;
    if (depth >= 3) return true;
  }
  return false;
}

function isSolidAt(grid: GameState['grid'], c: number, r: number): boolean {
  if (c < 0 || c >= grid.cols || r < 0 || r >= grid.rows) return true;
  return grid.tiles[r * grid.cols + c] !== 0;
}

/**
 * 動く用事が無い時、猫がその場で始めること。
 * 猫は一日の大半をこうして過ごすので、ここが薄いと「せわしない小動物」に見える。
 */
function startResting(state: GameState, cat: CatState, n: { c: number; r: number }, rng: RngState): void {
  const roll = rngFloat(rng);
  const nearWindow =
    Math.abs(n.c - state.entrance.c) <= 2 && Math.abs(n.r - state.entrance.r) <= 2;

  if (nearWindow || (isPerch(state, n.c, n.r) && roll < 0.55)) {
    // 高いところの縁や窓辺で、外を眺める
    cat.busy = REST_MIN + rngFloat(rng) * (REST_MAX - REST_MIN);
    cat.busyKind = 'watch';
    return;
  }
  if (roll < 0.25) {
    cat.busy = 2 + rngFloat(rng) * 2.5;
    cat.busyKind = 'stretch';
    return;
  }
  if (roll < 0.6) {
    cat.busy = 6 + rngFloat(rng) * 12;
    cat.busyKind = 'groom';
    return;
  }
  cat.busy = REST_MIN + rngFloat(rng) * (REST_MAX - REST_MIN);
  cat.busyKind = 'loaf';
}

/** 着いた先で何を始めるか（食べる・飲む・寝る・くつろぐ） */
function arrive(
  state: GameState,
  graph: JumpGraph,
  cat: CatState,
  species: Species,
  rng: RngState,
): void {
  const n = graph.nodes.get(cat.node);
  if (!n) return;
  if (cat.thirst >= THIRST_DUE && atSpot(state.water, n.c, n.r)) {
    cat.busy = DRINK_SEC;
    cat.busyKind = 'drink';
    cat.stuck = false;
    return;
  }
  if (cat.hunger >= HUNGER_DUE && atSpot(state.bowl, n.c, n.r)) {
    cat.busy = EAT_SEC;
    cat.busyKind = 'eat';
    cat.stuck = false;
    return;
  }
  if (cat.toy !== null && salvageAt(state.grid, cat.toy % state.grid.cols, (cat.toy / state.grid.cols) | 0) > 0) {
    cat.busy = PLAY_MIN + rngFloat(rng) * (PLAY_MAX - PLAY_MIN);
    cat.busyKind = 'play';
    return;
  }
  if (cat.swat !== null && fixtureInReachAt(state, n.c, n.r, species.paw) !== null) {
    // 届く物が変わっていることもあるので、今そこから届く物に狙いを付け直す
    cat.swat = fixtureInReachAt(state, n.c, n.r, species.paw);
    cat.busy = SWAT_MIN + rngFloat(rng) * (SWAT_MAX - SWAT_MIN);
    cat.busyKind = 'swat';
    return;
  }
  cat.swat = null;
  if (furnitureAt(state.grid, n.c, n.r) === BED) {
    cat.busy = SLEEP_MIN + rngFloat(rng) * (SLEEP_MAX - SLEEP_MIN);
    cat.busyKind = 'sleep';
    cat.slept = true;
    return;
  }
  // 用事が無ければ、その場でくつろぐ
  startResting(state, cat, n, rng);
}

/**
 * 経路から次の一歩を取り出す。
 * 取り出す時に、そのノードがまだグラフに在るか必ず確認する（§5.1 防御2）。
 */
function nextLeg(
  state: GameState,
  graph: JumpGraph,
  cat: CatState,
  species: Species,
  rng: RngState,
): void {
  if (!cat.path || cat.step >= cat.path.length - 1) {
    cat.path = null;
    cat.goal = null;
    cat.waiting = 0;
    arrive(state, graph, cat, species, rng);
    cat.goalReason = cat.stuck ? cat.goalReason : 'none';
    cat.mode = 'idle';
    cat.wait = cat.busy > 0 ? cat.busy : IDLE_MIN + rngFloat(rng) * (IDLE_MAX - IDLE_MIN);
    return;
  }
  const aKey = cat.path[cat.step];
  const bKey = cat.path[cat.step + 1];
  const a = graph.nodes.get(aKey);
  const b = graph.nodes.get(bKey);
  if (!a || !b) {
    // 途中のマスが消えていた。その場で降りて、探し直す
    cat.path = null;
    snapToGround(state, graph, cat);
    cat.startled = STARTLE_SEC;
    cat.wait = 0.4;
    return;
  }

  // 前が詰まっている。少し待つ（§2.1「同じ棚に3匹が乗ろうとして押し合っている」）
  if (cat.waiting < SQUEEZE_AFTER && occupiedByOther(state, cat, bKey)) {
    cat.mode = 'idle';
    cat.wait = WAIT_STEP;
    cat.waiting += WAIT_STEP;
    return;
  }
  cat.waiting = 0;

  cat.from = aKey;
  cat.to = bKey;
  cat.t = 0;
  const dr = b.r - a.r;
  cat.mode = dr < 0 ? 'jump' : dr > 0 ? 'fall' : 'walk';

  const dist = Math.hypot(b.c - a.c, dr);
  cat.dur =
    cat.mode === 'walk'
      ? 0.44 * Math.abs(b.c - a.c) + 0.08
      : cat.mode === 'jump'
        ? 0.34 + dist * 0.06
        : 0.26 + dist * 0.045;
  if (b.c !== a.c) cat.face = b.c > a.c ? 1 : -1;
  cat.step++;
  cat.node = bKey;
}

/**
 * 資材は猫が習性として勝手に生む（§3.4）。
 * 爪とぎ跡と抜け毛。発生した場所に痕跡が残り、**その場に少しだけ落ちる**（§10.8）。
 * 猫は拾わないので、要るならプレイヤーが押して拾う。
 */
function leaveTrace(state: GameState, cat: CatState, rng: RngState): void {
  const k = cat.node;
  if (k >= 0 && k < state.traces.length) state.traces[k] += 1;
  const c = k % state.grid.cols;
  const r = (k / state.grid.cols) | 0;
  addDropTo(state, c, r, { cloth: 1 });
  cat.nextTrace = TRACE_MIN + rngFloat(rng) * (TRACE_MAX - TRACE_MIN);
}

/**
 * 今この猫が、足元の設置物をどれくらい傷ませるか（§3.5）。
 * 実際に傷ませるのは tick 側（World を触る必要があるため）。
 */
export function wearRate(cat: CatState): number {
  if (cat.mode !== 'idle') return 0;
  let rate = WEAR_IDLE;
  if (cat.waiting > 0) rate += WEAR_QUEUED;
  if (cat.stuck) rate += WEAR_STUCK;
  return rate;
}

export function updateCat(
  state: GameState,
  graph: JumpGraph,
  cat: CatState,
  species: Species,
  dt: number,
  rng: RngState,
): void {
  cat.hunger += dt;
  cat.thirst += dt;
  if (cat.arriving > 0) cat.arriving = Math.max(0, cat.arriving - dt);
  cat.nextTrace -= dt;
  // 落ち着いている時にだけ痕跡を残す（移動中の空中で爪をとがない）
  if (cat.nextTrace <= 0 && cat.mode === 'idle') leaveTrace(state, cat, rng);
  cat.blocked = cat.stuck ? cat.blocked + dt : 0;
  if (cat.startled > 0) cat.startled = Math.max(0, cat.startled - dt);

  if (cat.mode === 'idle') {
    cat.sit += dt;
    if (cat.busy > 0) {
      cat.busy = Math.max(0, cat.busy - dt);
      if (cat.busy === 0) {
        if (cat.busyKind === 'eat') cat.hunger = 0;
        else if (cat.busyKind === 'drink') cat.thirst = 0;
        else if (cat.busyKind === 'play') cat.toy = null;
        cat.busyKind = null;
        // ひと眠りしたあとは伸びをする
        if (cat.slept) {
          cat.slept = false;
          cat.busy = 2 + rngFloat(rng) * 2;
          cat.busyKind = 'stretch';
          cat.wait = cat.busy;
        }
        cat.goalReason = 'none';
      }
    }
    // 面食らっている間は動き出さない
    cat.wait -= cat.startled > 0 ? dt * 0.4 : dt;
    if (cat.wait <= 0) {
      // 経路を持ったまま待っていた（詰まり）なら、その続きから
      if (cat.path) nextLeg(state, graph, cat, species, rng);
      else startMove(state, graph, cat, species, rng);
    }
    return;
  }

  cat.sit = 0;
  const from = cat.from !== null ? graph.nodes.get(cat.from) : undefined;
  const to = cat.to !== null ? graph.nodes.get(cat.to) : undefined;
  if (!from || !to) {
    cat.mode = 'idle';
    cat.wait = 0.3;
    return;
  }

  cat.t += dt * species.speed;
  const u = Math.min(1, cat.dur > 0 ? cat.t / cat.dur : 1);
  const ax = from.c * TILE + TILE / 2;
  const ay = (from.r + 1) * TILE;
  const bx = to.c * TILE + TILE / 2;
  const by = (to.r + 1) * TILE;

  cat.x = ax + (bx - ax) * u;
  if (cat.mode === 'walk') {
    cat.y = ay + (by - ay) * u;
  } else if (cat.mode === 'jump') {
    const rise = ay - by + TILE * 0.55;
    cat.y = ay - rise * Math.sin(u * Math.PI * 0.5) + (rise - (ay - by)) * (u * u);
  } else {
    const e = u * u;
    cat.y = ay + (by - ay) * e - TILE * 0.25 * Math.sin(u * Math.PI) * (1 - u);
  }

  if (u >= 1) {
    cat.x = bx;
    cat.y = by;
    nextLeg(state, graph, cat, species, rng);
  }
}

/**
 * 猫同士のじゃれ合い（§3.3 のような能力差ではなく、ただの猫らしさ）。
 * 隣り合ってのんびりしている2匹が、たまに転がって遊びだす。
 * プレイヤーは仕向けられない。見かけたら得、くらいの位置づけ。
 */
export function maybeTussle(state: GameState, dt: number, rng: RngState): void {
  for (let i = 0; i < state.cats.length; i++) {
    const a = state.cats[i];
    if (!canTussle(a)) continue;
    for (let j = i + 1; j < state.cats.length; j++) {
      const b = state.cats[j];
      if (!canTussle(b)) continue;
      if (Math.abs(a.x - b.x) > TILE * 1.6) continue;
      if (Math.abs(a.y - b.y) > TILE * 0.6) continue;
      // だいたい20秒に1回くらい始まる
      if (rngFloat(rng) > dt / 20) continue;
      const dur = TUSSLE_MIN + rngFloat(rng) * (TUSSLE_MAX - TUSSLE_MIN);
      for (const cat of [a, b]) {
        cat.busy = dur;
        cat.busyKind = 'tussle';
        cat.wait = dur;
        cat.path = null;
      }
      a.face = a.x <= b.x ? 1 : -1;
      b.face = -a.face as 1 | -1;
      return; // 1 tick に1組まで
    }
  }
}

/**
 * 猫の集会（§2.1 の実践）。
 * 近くにのんびりしている猫が3匹たまると、そのまま座り込んで長い時間動かない。
 * プレイヤーは仕向けられないし、集会に何かの効果があるわけでもない。
 * 「ここは居心地がいい／ここは混んでいる」が数字を出さずに読めればそれでよい。
 */
export function maybeGathering(state: GameState, dt: number, rng: RngState): void {
  for (let i = 0; i < state.cats.length; i++) {
    const a = state.cats[i];
    if (!canTussle(a) || a.busyKind === 'meet') continue;
    const group = [a];
    for (const b of state.cats) {
      if (b.id === a.id) continue;
      if (!canTussle(b) || b.busyKind === 'meet') continue;
      if (Math.abs(a.x - b.x) > TILE * MEET_RANGE) continue;
      if (Math.abs(a.y - b.y) > TILE * 0.6) continue;
      group.push(b);
    }
    if (group.length < 3) continue;
    // だいたい25秒に1回くらい始まる
    if (rngFloat(rng) > dt / 25) continue;

    let cx = 0;
    for (const cat of group) cx += cat.x;
    cx /= group.length;
    const dur = MEET_MIN + rngFloat(rng) * (MEET_MAX - MEET_MIN);
    for (const cat of group) {
      cat.busy = dur;
      cat.busyKind = 'meet';
      cat.wait = dur;
      cat.path = null;
      cat.face = cat.x <= cx ? 1 : -1;
    }
    return; // 1 tick に1組まで
  }
}

function canTussle(cat: CatState): boolean {
  return (
    cat.mode === 'idle' &&
    cat.busyKind !== 'sleep' &&
    cat.busyKind !== 'eat' &&
    cat.busyKind !== 'drink' &&
    cat.busyKind !== 'tussle' &&
    cat.busyKind !== 'play' &&
    pendingNeed(cat) === null &&
    cat.startled <= 0
  );
}

/** 地形が変わった時に呼ぶ。経路を捨て、立てない猫を引き戻す（§5.1 防御1・3） */
export function repairCat(state: GameState, graph: JumpGraph, cat: CatState): void {
  const legBroken =
    cat.mode !== 'idle' &&
    (cat.to === null ||
      !graph.nodes.has(cat.to) ||
      (cat.from !== null && !graph.nodes.has(cat.from)));
  const hadPlan = cat.path !== null;
  cat.path = null;
  cat.goal = null;
  cat.goalReason = 'none';
  cat.waiting = 0;
  // 道が変わったのだから、届かない判定はやり直す
  cat.stuck = false;

  if (!graph.nodes.has(cat.node) || legBroken) {
    snapToGround(state, graph, cat);
    cat.startled = STARTLE_SEC;
    cat.wait = 0.3;
    return;
  }
  // 立ち位置は無事。ただし予定は消えたので、着いたら探し直す。
  // 予定を持っていた猫だけが「工事の邪魔をされた」顔になる。
  if (hadPlan) {
    cat.startled = STARTLE_SEC;
    if (cat.mode === 'idle') cat.wait = Math.max(cat.wait, 0.3);
  }
}
